import {
  Box,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import queryString from 'query-string';
import React, { useEffect, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import { API_CLIENTS_USERS_LIST_URL } from '../../constants/apiUrls';
import { CLIENTS_USERS_EDIT_URL } from '../../constants/routeUrls';
import { useConfig } from '../../hooks/useConfig';
import { getRequestJSON } from '../../util/services';
import { headerStyles } from '../Contracts/ContractsTable';
import SsoIdCell from '../shared/SsoIdCell';

interface UsersListProps {
  clientId: string;
  clientSsoId?: number | null;
}

function UsersList(props: UsersListProps) {
  const { apiHost } = useConfig();
  const [usersList, setUsersList] = useState([]);
  const [paginationState, setPagination] = useState({
    total_items: 0,
    per_page: 5,
  });
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const { clientId, clientSsoId } = props;

  useEffect(() => {
    const getItems = async () => {
      const path = generatePath(API_CLIENTS_USERS_LIST_URL, {
        id: clientId,
        '*': `?${queryString.stringify({
        // Server page is 1-index and front-end is 0-index based.
        page: `${page + 1}`,
      })}`,
      });
      setIsLoading(true);
      const response = await getRequestJSON(apiHost, path);
      setIsLoading(false);

      if (!response) {
        return;
      }

      const { collection, pagination: paginationInfo } = response;

      setUsersList(collection);
      setPagination(paginationInfo);
    };

    getItems();
  }, [page, apiHost, clientId]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const headerStylesCss = headerStyles();

  return (
    <Box py={2}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row' }}>
        <Stack direction="row" alignItems="center">
          <Typography variant="h5">Users</Typography>
        </Stack>
      </Box>
      <Box mt={2}>
        <TableContainer className={headerStylesCss.root}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead sx={{}}>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell align="left">SSO ID</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Loading Skeleton */}
              {isLoading &&
                [...Array(50)].map((item, idx) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <TableRow key={idx} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell scope="row">
                      <Skeleton animation="wave" width={500} variant="text" sx={{ fontSize: '1rem' }} />
                    </TableCell>
                    <TableCell align="left">
                      <Skeleton animation="wave" width={64} variant="text" sx={{ fontSize: '1rem' }} />
                    </TableCell>
                  </TableRow>
                ))}
              {usersList &&
                usersList.length > 0 &&
                usersList.map((user) => (
                    <TableRow
                      key={`link-${user.id}`}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      onClick={() =>
                        navigate(
                          generatePath(CLIENTS_USERS_EDIT_URL, {
                            id: clientId,
                            userId: user.id,
                          })
                        )
                      }
                    >
                      <TableCell scope="row">
                        {user.name} ({user.id})
                      </TableCell>
                      <SsoIdCell kind="user" entity={user} orgSsoId={clientSsoId} />
                    </TableRow>
                  ))}
              <TableRow key="pagination">
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  count={paginationState.total_items} // served static by backend
                  page={page}
                  onPageChange={handlePageChange}
                  rowsPerPage={paginationState.per_page} // served static by backend
                  onRowsPerPageChange={() => {}}
                />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}

export default UsersList;
